import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, Send } from "lucide-react";
import { useDispatch } from "react-redux";
import { setStartApplication } from "../../../stores/educationSlice";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useEduChildModuleData } from "@/module/education/helpers/useEduChildModuleData";
import AttachCourseModal from "@/features/leads/components/view-lead/components/Ongoing/components/AttachCourseModal";
import { refetchChilds } from "@/features/leads/api/useChilds";
import { refetchCurrentChild } from "@/features/leads/api/useCurrentChild";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import usePipeline from "@/features/stage/api/usePipeline";
import { dealStageIdOf, useCanEditDeal } from "@/rbac/useCanEditDeal";

/**
 * NO CAP ON DEALS PER LEAD (user directive 2026-09-09: "completely remove
 * the limit… no need of the limits").
 *
 * This component used to refuse a new deal once the lead had `Stage.maxChilds`
 * of them — falling back to a hardcoded 5 when the stage carried no value,
 * which is what actually fired: the block came from the FIRST child stage, not
 * the stage the admin had set to 8, so a lead was refused at 6 deals. It was
 * also the ONLY such gate: the server never caps deal creation
 * (`ChildCreation` has no count check), so removing it here removes it
 * outright. `Stage.maxChilds` still exists and is still honoured by the
 * pipeline-MIGRATION pre-flight (`pipeline-migration.service.ts`), which is a
 * different question — "will these deals fit the target pipeline".
 */

/** The pipeline rows this component reads (the API response is untyped). */
interface PipelineStageLike {
  name?: string | null;
  order?: number | null;
  isChild?: boolean | null;
}

const makeFirstLetterCapital = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export default function ActionForStartApplication() {
  const { lead, child, isChildActive } = useActiveDataMutation();
  const { courseName } = useEduChildModuleData(child);
  const { pipeline } = usePipeline();

  // The first stage that allows children — where a new deal actually lands
  // (ChildCreation.executeCore on the server). Used for the button LABEL only.
  const firstChildStage = useMemo<PipelineStageLike | null>(() => {
    if (!Array.isArray(pipeline)) return null;
    return (
      (pipeline as PipelineStageLike[])
        .filter((s) => s?.isChild)
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))[0] ?? null
    );
  }, [pipeline]);

  const dispatch = useDispatch();
  const [attachModalOpen, setAttachModalOpen] = useState(false);

  const handleStartApplication = () => {
    dispatch(setStartApplication(true));
  };

  // Two-button mode: a child is selected on the lead but has no course
  // attached yet. Users hit this when they opened a Course detail while
  // standing on a fresh deal — without this branch the only option was to
  // spawn a *new* deal, leaving the empty one orphaned.
  const childNeedsCourseAttach = isChildActive && !!child?.id && !courseName;
  // Attaching to the EXISTING deal is a deal edit ("Edit choices" on its own
  // stage); "Start new …" is deal create and stays as it is.
  const canEditDeal = useCanEditDeal();
  const canAttachToCurrent =
    childNeedsCourseAttach && canEditDeal(dealStageIdOf(child));

  // The new deal always lands at the first stage where children are allowed
  // (`isChild: true`), regardless of where the lead currently sits — see
  // ChildCreation.executeCore on the server. The button label has to track
  // that stage, not `lead.stage.name`, or it lies to the operator ("Start
  // Pre Application" while the deal actually starts at Qualify).
  const firstChildStageName = firstChildStage?.name ?? null;

  const stageLabel = makeFirstLetterCapital(
    firstChildStageName || lead?.stage?.name || "Application"
  );

  const handleAttachClose = () => {
    setAttachModalOpen(false);
    Promise.all([refetchChilds(), refetchCurrentChild(), refetchCurrentLead()]);
  };

  return (
    <div className="bg-white flex gap-4 items-center justify-end w-full">
      {canAttachToCurrent && (
        <Button
          variant="outline"
          className="flex gap-1.5 items-center justify-center px-4 py-2.5 rounded-[12px]"
          onClick={() => setAttachModalOpen(true)}
        >
          <Paperclip size={20} />
          <span className="font-medium text-[14px] tracking-[-0.28px] leading-normal">
            Attach to current
          </span>
        </Button>
      )}

      <Button
        variant="primary"
        className={`flex gap-1.5 items-center justify-center px-4 py-2.5 rounded-[12px] ${
          childNeedsCourseAttach ? "" : "w-[204px]"
        }`}
        onClick={handleStartApplication}
      >
        <Send size={24} />
        <span className="font-medium text-[16px] tracking-[-0.32px] leading-normal">
          {childNeedsCourseAttach ? `Start new ${stageLabel}` : `Start ${stageLabel}`}
        </span>
      </Button>

      {canAttachToCurrent && (
        <AttachCourseModal
          open={attachModalOpen}
          onClose={handleAttachClose}
          dealId={Number(child.id)}
          dealName={child.name || "Deal"}
        />
      )}
    </div>
  );
}
